/**
 * The online table: one row per LEG.
 *
 * `ALA-NQZ` is two rows — a DEPARTURE row belonging to ALA and an ARRIVAL row
 * belonging to NQZ. They share a flight number, a route and a `departedTime`, and
 * nothing else: each leg carries its own scheduled/estimated/actual times, its own
 * status (`DEPARTED_LATE` against `LANDED_LATE`), its own stand and its own delay.
 * A board for one airport therefore filters on `airportIataCode`; a search by
 * flight number returns both legs, and that is correct rather than a duplicate.
 */

export const FLIGHT_TYPES = ['ARRIVAL', 'DEPARTURE'] as const;
export type FlightType = (typeof FLIGHT_TYPES)[number];

export const FLIGHT_SCOPES = ['ALL', 'DOMESTIC', 'INTERNATIONAL'] as const;
export type FlightScope = (typeof FLIGHT_SCOPES)[number];

export const FLIGHT_STATUSES = [
  'EXPECTED',
  'ON_TIME',
  'DELAYED',
  'CHECK_IN',
  'BOARDING',
  'DEPARTED_EARLY',
  'DEPARTED_ON_TIME',
  'DEPARTED_LATE',
  'LANDED_EARLY',
  'LANDED_ON_TIME',
  'LANDED_LATE',
  'CANCELLED',
  'UNKNOWN',
] as const;
export type FlightStatus = (typeof FLIGHT_STATUSES)[number];

/** A row of the table. */
export interface FlightRow {
  id: number;
  airportIataCode: string | null;
  flightNumber: string | null;
  airlineIataCode: string | null;
  flightType: FlightType | null;
  /**
   * NOT a property of the row: the server echoes back whatever scope was filtered
   * by, so asking for DOMESTIC makes every row say DOMESTIC and asking for nothing
   * makes every row say null — including rows that plainly are domestic. Never
   * display it. Compare the two airport codes instead.
   */
  flightScope: FlightScope | null;
  flightStatus: FlightStatus | null;
  departureAirportIataCode: string | null;
  arrivalAirportIataCode: string | null;
  /** The published time: STA on an arrival row, STD on a departure row. */
  scheduledDate: string | null;
  /** The latest expectation. Empty until some source gives one. */
  estimatedDate: string | null;
  /** What happened: touchdown on an arrival, wheels-up on a departure. */
  actualDate: string | null;
  /** When the flight left its origin — present on BOTH legs. */
  departedTime: string | null;
  passengerAmount: number | null;
  totalCargoWeight: number | null;
  planeParkingName: string | null;
  delayMinutes: number | null;
  /** An IATA delay code, when a source reported one. */
  delayReason: string | null;
}

/**
 * The request body. Filter, page and sort in one object, every field optional.
 *
 * Text fields match `LIKE %value%`, case-insensitively: `"A"` matches ALA, AKX and
 * SCO alike. The enums are exact. `passengerAmount` and `totalCargoWeight` are
 * EQUALITY, not minimums — which makes them nearly useless as filters, so the table
 * does not offer them.
 */
export interface ScheduleCriteria {
  flightNumber?: string;
  airlineIataCode?: string;
  airportIataCode?: string;
  departureAirportIataCode?: string;
  arrivalAirportIataCode?: string;
  /** Filtered as `planeParking`; SORTED as `planeParkingName`. Not a typo. */
  planeParking?: string;
  flightType?: FlightType;
  flightStatus?: FlightStatus;
  flightScope?: FlightScope;
  /**
   * The multi-select forms: a row matches when its value equals ANY element. Exact,
   * unlike the single airport fields above — `["AL"]` finds nothing, `["ALA"]` finds
   * Almaty — with case and surrounding spaces ignored. Empty means no filter.
   *
   * A single field sent WITH its array applies both conditions, so the board sends
   * these and never the singles.
   */
  flightStatuses?: FlightStatus[];
  departureAirportIataCodes?: string[];
  arrivalAirportIataCodes?: string[];
  scheduledDateStart?: string;
  scheduledDateEnd?: string;
  estimatedDateStart?: string;
  estimatedDateEnd?: string;
  actualDateStart?: string;
  actualDateEnd?: string;
  pageNum?: number;
  rows?: number;
  sortBy?: SortableField;
  sortDirection?: 'ascending' | 'descending';
}

/**
 * The only names `sortBy` accepts.
 *
 * A name outside this list goes straight into the JPA query and comes back as a
 * **500**, not a 400 — so the type is the guard. Note the pair that does not match:
 * the stand filters as `planeParking` and sorts as `planeParkingName`.
 */
export const SORTABLE_FIELDS = [
  'id',
  'created',
  'updated',
  'airportIataCode',
  'flightType',
  'flightStatus',
  'departureAirportIataCode',
  'arrivalAirportIataCode',
  'airlineIataCode',
  'flightNumber',
  'scheduledDate',
  'estimatedDate',
  'actualDate',
  'departedTime',
  'passengerAmount',
  'totalCargoWeight',
  'planeParkingName',
  'delayMinutes',
  'delayReason',
  'externalFlightId',
] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

export const isSortableField = (value: unknown): value is SortableField =>
  typeof value === 'string' && (SORTABLE_FIELDS as readonly string[]).includes(value);

/**
 * How a status is coloured.
 *
 * The tone is a hint; the badge always carries the translated status name, so
 * nothing is lost in greyscale or to a reader who cannot separate the tints.
 */
export const statusTone = (
  status: FlightStatus | null,
): 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case 'ON_TIME':
    case 'DEPARTED_ON_TIME':
    case 'DEPARTED_EARLY':
    case 'LANDED_ON_TIME':
    case 'LANDED_EARLY':
      return 'success';
    case 'DELAYED':
    case 'DEPARTED_LATE':
    case 'LANDED_LATE':
      return 'warning';
    case 'CANCELLED':
      return 'danger';
    case 'CHECK_IN':
    case 'BOARDING':
      return 'accent';
    case 'EXPECTED':
      return 'info';
    // UNKNOWN, and a row whose status is null. There is no longer a bare DEPARTED or
    // LANDED to land here: every movement carries its punctuality now, so those two
    // had their own cases until the server dropped them.
    default:
      return 'neutral';
  }
};

/**
 * The real scope of a flight, worked out from its own airports.
 *
 * The server's `flightScope` cannot answer this — it only repeats the filter — so
 * the route decides: a leg with either end outside Kazakhstan is international.
 * IATA codes for Kazakh airports are not enumerable from here, so this is a
 * best-effort hint for display, never a filter.
 */
export const KZ_AIRPORTS = new Set([
  'NQZ', 'ALA', 'CIT', 'DMB', 'AKX', 'GUW', 'KGF', 'KSN', 'KZO', 'PPK', 'PWQ',
  'SCO', 'PLX', 'URA', 'UKK', 'HSA', 'BXH', 'DZN', 'EKB', 'AYK', 'KOV', 'ZKO',
]);

export const routeScope = (row: FlightRow): 'DOMESTIC' | 'INTERNATIONAL' | null => {
  const from = row.departureAirportIataCode;
  const to = row.arrivalAirportIataCode;
  if (!from || !to) return null;

  return KZ_AIRPORTS.has(from) && KZ_AIRPORTS.has(to) ? 'DOMESTIC' : 'INTERNATIONAL';
};

/**
 * One flight in full, from `GET /online-table/id/{id}`.
 *
 * The table projection plus the audit fields. Note what is NOT here: `onBlockTime`,
 * `baggagePieces` and `aircraftType` exist on the response object but always read
 * back as null — the schedule table has no column for them, so they land in
 * `international_arrival_idx` on the way in and never come back. Declaring them
 * would promise a value that cannot arrive, so the panel does not show them.
 *
 * The row's owning integration is not exposed here either. Its history answers
 * that: every change names the source that made it.
 */
export interface FlightDetail extends FlightRow {
  created: string | null;
  updated: string | null;
  active: boolean | null;
  /** The sender's own id for the flight, when a source supplied one. */
  externalFlightId: string | null;
  flightRadarId: string | null;
}

/** What happened to a row, and who did it. */
export type HistoryEvent = 'CREATED' | 'UPDATED' | 'DELETED';

export interface FieldChange {
  /** The field name as `AirportScheduleInfo` spells it, so it matches a column. */
  field: string;
  /** Absent on a row projected from the frozen archive, which stored only the new value. */
  oldValue: string | null;
  newValue: string | null;
}

/**
 * One change to a flight, from `GET /online-table/history/scheduleId/{id}`.
 *
 * These are the integration events for the row, oldest first. Rows older than the
 * changeover come from a frozen archive and are projected into the same shape: their
 * event is always UPDATED, their source the coarse `AIRPORT`, their changes carry a
 * new value with no old one, and they know no message.
 */
export interface FlightHistoryRow {
  /** Unique within events and within the archive — but the two do not share an id
   *  space, so this keys a row rather than addressing one. */
  eventId: number;
  scheduleId: number;
  modifiedDate: string | null;
  event: HistoryEvent | null;
  /** `NQZ_FTP`, `MVT_AIRASTANA`, `FLIGHTRADAR`, `EFREIGHT`, or `AIRPORT` for archived. */
  source: string | null;
  flightNumber: string | null;
  fromAirport: string | null;
  toAirport: string | null;
  /** The whole change as one line, for reading. */
  details: string | null;
  /** The same change field by field, for rendering. */
  changedFields: FieldChange[] | null;
  /** The sender's own reference for the message behind the change. */
  messageRef: string | null;
  /** The delivery in the integration monitor. Null on archived rows. */
  messageId: number | null;
  /** The message as received. Null once retention has cleared it — after 14 days,
   *  and always for a poll whose content had not changed. */
  raw: string | null;
}

/** The source the frozen archive is projected under. */
export const ARCHIVE_SOURCE = 'AIRPORT';

/** Where a flight is, for the panel's illustration. */
export type FlightPhase = 'before' | 'air' | 'after' | 'cancelled';

export const phaseOf = (status: FlightStatus | null): FlightPhase => {
  if (status === 'CANCELLED') return 'cancelled';
  if (status === null) return 'before';
  if (status.startsWith('DEPARTED')) return 'air';
  if (status.startsWith('LANDED')) return 'after';
  return 'before';
};
